import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ShieldCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { trackUxInteraction } from '../../services/uxTelemetry';

const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;

const ComplianceDashboardNudge = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const trackedSuggestionRef = useRef(null);

  const canViewCompliance = useMemo(() => {
    const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
    return permissions.includes('compliance.view') || permissions.includes('compliance.manage');
  }, [user?.permissions]);

  const clinicId = String(
    user?.active_clinic_id || user?.default_clinic_id || user?.id || 'clinic'
  );

  useEffect(() => {
    let active = true;

    if (!user || !canViewCompliance) {
      setSuggestion(null);
      return () => {
        active = false;
      };
    }

    api
      .get('/compliance')
      .then((response) => {
        if (!active) return;

        const compliance = response?.data || {};
        const nextItem = compliance?.next_item;
        const pendingAssessment = compliance?.pending_assessment;

        if (pendingAssessment && !compliance?.is_configured) {
          setSuggestion({
            id: `assessment:${pendingAssessment.id}`,
            title: 'Continue o diagnóstico que você começou',
            firstStep:
              'Confirme algumas informações da clínica para transformar seu resultado em orientações práticas.',
          });
          return;
        }

        if (!compliance?.is_configured || !nextItem) {
          setSuggestion(null);
          return;
        }

        setSuggestion({
          id: `item:${nextItem.id}`,
          title: nextItem.title,
          firstStep:
            nextItem.first_step ||
            'Abra Boas Práticas para ver o próximo passo recomendado para sua clínica.',
        });
      })
      .catch(() => {
        if (active) setSuggestion(null);
      });

    return () => {
      active = false;
    };
  }, [canViewCompliance, user]);

  const storageKey = suggestion
    ? `compliance_dashboard_nudge:${clinicId}:${suggestion.id}`
    : null;

  useEffect(() => {
    if (!storageKey) {
      setDismissed(false);
      return;
    }

    try {
      const dismissedAt = Number(localStorage.getItem(storageKey));
      setDismissed(Boolean(dismissedAt && Date.now() - dismissedAt < DISMISS_FOR_MS));
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!suggestion || dismissed || trackedSuggestionRef.current === suggestion.id) return;
    try {
      const dismissedAt = Number(localStorage.getItem(storageKey));
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_FOR_MS) return;
    } catch {
      // A ausência de armazenamento local não impede a exibição da orientação.
    }
    trackedSuggestionRef.current = suggestion.id;
    trackUxInteraction({
      event_name: 'compliance_nudge_viewed',
      module_id: 'compliance',
      source: 'dashboard',
    });
  }, [dismissed, storageKey, suggestion]);

  const dismiss = () => {
    trackUxInteraction({
      event_name: 'compliance_nudge_dismissed',
      module_id: 'compliance',
      source: 'dashboard',
    });
    setDismissed(true);
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // Mantém o adiamento apenas durante esta sessão.
    }
  };

  if (!suggestion || dismissed) return null;

  return (
    <section className="order-1 rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm sm:p-5">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-emerald-100 p-2 text-emerald-700">
          <ShieldCheck className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Boas Práticas
          </p>
          <h2 className="mt-1 text-base font-semibold text-gray-900">
            Uma ação para deixar sua clínica mais preparada
          </h2>
          <p className="mt-2 font-medium text-gray-900">{suggestion.title}</p>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600">
            {suggestion.firstStep}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                trackUxInteraction({
                  event_name: 'compliance_nudge_opened',
                  module_id: 'compliance',
                  source: 'dashboard',
                });
                navigate('/conformidade');
              }}
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              Continuar em Boas Práticas
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Lembrar depois
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-600"
          aria-label="Lembrar desta ação depois"
          title="Lembrar depois"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
};

export default ComplianceDashboardNudge;
